import * as React from "react"
import { SlidersHorizontal } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { OPTIONAL_COLUMNS, type ColumnKey } from "@/components/alfred/alfred-devices-table"

interface ColumnsToggleProps {
  visibleColumns: Set<ColumnKey>
  onChange: (columns: Set<ColumnKey>) => void
}

export function ColumnsToggle({ visibleColumns, onChange }: ColumnsToggleProps) {
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener("scroll", close, { passive: true })
    return () => window.removeEventListener("scroll", close)
  }, [open])

  const toggle = (key: ColumnKey) => {
    const next = new Set(visibleColumns)
    if (next.has(key)) { next.delete(key) } else { next.add(key) }
    onChange(next)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-tour="choose-columns"
          className="bg-card border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2 bg-popover border-border" align="end" collisionPadding={{ top: 112 }}>
        <p className="text-xs text-muted-foreground px-2 pb-2 font-medium">Toggle columns</p>
        <div className="space-y-0.5 max-h-[27rem] overflow-y-auto">
          {OPTIONAL_COLUMNS.map((col) => (
            <div
              key={col.key}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary cursor-pointer"
              onClick={() => toggle(col.key)}
            >
              <Checkbox
                checked={visibleColumns.has(col.key)}
                onCheckedChange={() => toggle(col.key)}
                onClick={(e) => e.stopPropagation()}
                className="border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-foreground">{col.label}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
